import React from 'react';
import ScorecardResultsSummary from './results/Summary';
import ChevronRightIcon from 'material-ui/svg-icons/navigation/chevron-right';
import ChevronLeftIcon from 'material-ui/svg-icons/navigation/chevron-left';
import throttle from 'lodash/throttle';
import range from 'lodash/range';
import ScorecardResultsUserScoreCircularCardLabel from 'components/pure/scorecards/results/ScorecardResultsUserScoreCircularCardLabel';
import ScorecardResultsUserScoreColumnHeader from 'components/pure/scorecards/ScorecardResultsUserScoreColumnHeader';
import ScorecardResultsUserNoScoreColumnHeader from 'components/pure/scorecards/ScorecardResultsUserNoScoreColumnHeader';
import ConstValues from 'utils/const';
import 'styles/scorecards/ScorecardResultsGrid.styl';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import CommentIcon from 'components/pure/scorecards/icons/CommentIcon';
import BulbIcon from 'components/pure/scorecards/icons/BulbIcon';
import classNames from 'classnames';
import GreyCardLabel from 'components/pure/utils/GreyCardLabel';

const USER_ROW_WIDTH = 100;

export default class ScorecardResultsGrid extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      userRowsWidth: 200,
      page: 0,
    };
    this.handleResize = throttle(this.handleResize.bind(this), 300);
  }

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Handles resize
   * Calculate width of user rows
   */
  handleResize() {
    if (this.userRows) {
      let userRowsWidth = this.userRows.clientWidth;
      this.setState({userRowsWidth: userRowsWidth});
    }
  }

  getAvailableUserRows() {
    return Math.floor(this.state.userRowsWidth / USER_ROW_WIDTH);
  }

  renderHeaderRow(results, availableSpaces) {
    const {sessionUser} = this.props;
    return (
      <div className="row header-row">
        <div className="first-column">
          <ScorecardResultsSummary
            title={this.props.title}
            scorecard={this.props.scorecard}
            className="first-column"
            sessionUser={sessionUser}
          />
        </div>
        <div className="user-rows" ref={c => (this.userRows = c)}>
          {results.map(result => (
            <div className="cell user-score-header" key={result.reviewerUserId}>
              <ScorecardResultsUserScoreColumnHeader
                result={result}
                showEditOverlay={
                  sessionUser.id === result.reviewerUserId && this.props.goToEditScreen
                }
                goToEditScreen={this.props.goToEditScreen}
              />
            </div>
          ))}
          {availableSpaces > results.size &&
            !this.props.hideEmptyColumns &&
            range(results.size, availableSpaces).map(i => (
              <div className="cell user-score-header" key={i}>
                <ScorecardResultsUserNoScoreColumnHeader />
              </div>
            ))}
        </div>
      </div>
    );
  }

  answerLabelSpanColor(userId) {
    if (userId === this.props.sessionUser.id) {
      return '#2B7BFF';
    }
    return ConstValues.cardLabelSpanDefaultColor;
  }

  renderQuestionAnswerRow(question, index, results, availableSpaces, scorecard, labelScheme) {
    return (
      <div key={question.id}>
        <div className="row question-answer-row">
          <div className="first-column">
            {index + 1}. {question.text}
          </div>
          {results.map(result => {
            const answer = result.answers.find(answer => answer.questionId === question.id);

            const color = this.answerLabelSpanColor(result.userId);
            return (
              <div className="cell" key={result.id}>
                <div className="column" />
                <div className="column">
                  <ScorecardResultsUserScoreCircularCardLabel
                    answer={answer}
                    spanColor={color}
                    scoringScheme={scorecard.scoringScheme}
                    labelScheme={labelScheme}
                  />
                </div>
                <div className={classNames('column', 'icons')}>
                  {answer && answer.comment && (
                    <GenericTooltip
                      tooltip={<p dangerouslySetInnerHTML={{__html: answer.comment}} />}>
                      <CommentIcon shrink={answer.comment && answer.reason} />
                    </GenericTooltip>
                  )}
                  {answer && answer.reason && (
                    <GenericTooltip tooltip={<p>{answer.reason}</p>}>
                      <BulbIcon shrink={answer.comment && answer.reason} />
                    </GenericTooltip>
                  )}
                </div>
              </div>
            );
          })}
          {availableSpaces > results.size &&
            !this.props.hideEmptyColumns &&
            range(results.size, availableSpaces).map(i => (
              <div className="cell" key={i}>
                <GreyCardLabel label={`...`} style={{lineHeight: 'auto'}} />
              </div>
            ))}
        </div>
      </div>
    );
  }

  render() {
    const {scorecard, sessionUser, labelScheme} = this.props;
    const availableSpaces = this.getAvailableUserRows();
    const {page} = this.state;
    const minIndex = page * availableSpaces;
    const maxIndex = (page + 1) * availableSpaces;
    const results = scorecard.results
      // current logged in user first
      .sortBy(result => sessionUser.id !== result.reviewerUserId)
      .slice(minIndex, maxIndex);
    return (
      <div className="results-grid">
        {this.renderHeaderRow(results, availableSpaces)}
        {scorecard.questions.map((question, index) =>
          this.renderQuestionAnswerRow(
            question,
            index,
            results,
            availableSpaces,
            scorecard,
            labelScheme
          )
        )}
        {page > 0 && (
          <div className="pager left" onClick={() => this.setState({page: page - 1})}>
            <ChevronLeftIcon />
          </div>
        )}
        {maxIndex < scorecard.results.size && (
          <div className="pager right" onClick={() => this.setState({page: page + 1})}>
            <ChevronRightIcon />
          </div>
        )}
        <div className="extra-wrapper">{this.props.children}</div>
      </div>
    );
  }
}
